import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import { Horse } from '@phosphor-icons/react';
import api, { apiBaseURL } from '../../services/api';
import { SMART_COLORS as COLORS, smartCardSx, smartPageSx } from '../../theme/smartStableTheme';

const COPY = {
  ar: {
    title: 'خيولي',
    subtitle: 'قائمة خيلك المسجلة فقط مع حالة السكن والمعلومات الأساسية.',
    search: 'بحث باسم الخيل أو رقم الشريحة',
    noLink: 'هذا الحساب غير مربوط بمالك. يرجى ربط المستخدم بسجل المالك.',
    noData: 'لا توجد خيل مرتبطة بهذا الحساب',
    total: 'إجمالي الخيل',
    withRfid: 'لها شريحة',
    accommodated: 'مسكنة',
    mares: 'إناث',
    stallions: 'ذكور',
    details: 'عرض التفاصيل',
    location: 'الموقع',
    horse: 'الخيل',
    gender: 'الجنس',
    breed: 'السلالة',
    color: 'اللون',
    records: 'سجل',
    rfid: 'RFID',
  },
  en: {
    title: 'My Horses',
    subtitle: 'Only your registered horses with accommodation status and key details.',
    search: 'Search by horse name or RFID',
    noLink: 'This account is not linked to an owner profile.',
    noData: 'No horses are linked to this account',
    total: 'Total horses',
    withRfid: 'With RFID',
    accommodated: 'Accommodated',
    mares: 'Mares',
    stallions: 'Stallions',
    details: 'View details',
    location: 'Location',
    horse: 'Horse',
    gender: 'Gender',
    breed: 'Breed',
    color: 'Color',
    records: 'records',
    rfid: 'RFID',
  },
};

const normalize = (value) => String(value || '').toLowerCase().trim();

const horseName = (horse, lang) =>
  lang === 'ar'
    ? horse.name_arabic || horse.name || '-'
    : horse.name || horse.name_arabic || '-';

const getImageSrc = (image) => {
  if (!image) return undefined;
  if (image.startsWith('data') || image.startsWith('http')) return image;
  return `${apiBaseURL}/uploads/${image}`;
};

const Metric = ({ icon, label, value, color = COLORS.gold, soft = COLORS.goldSoft, dir = 'ltr' }) => (
  <Paper elevation={0} sx={{ ...smartCardSx, p: 2 }}>
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="flex-start"
      sx={{ gap: 1.5, textAlign: dir === 'rtl' ? 'right' : 'left' }}
    >
      <Avatar sx={{ bgcolor: soft, color }}>{icon}</Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ color, fontWeight: 900 }}>
          {label}
        </Typography>
        <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900 }}>
          {value}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const ClientHorseList = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language === 'ar' ? 'ar' : 'en';
  const text = COPY[lang];
  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  const alignStart = lang === 'ar' ? 'right' : 'left';
  const ownerId = localStorage.getItem('owner_id');
  const [horses, setHorses] = useState([]);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!ownerId || ownerId === 'null' || ownerId === 'undefined') {
        setLoading(false);
        setError(text.noLink);
        return;
      }

      try {
        const { data } = await api.get(`/owners/${ownerId}/horses-with-details`);
        if (alive) setHorses(Array.isArray(data) ? data : []);
      } catch {
        if (alive) setError(text.noData);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => {
      alive = false;
    };
  }, [ownerId, text.noData, text.noLink]);

  const filtered = useMemo(() => {
    const q = normalize(query);
    if (!q) return horses;
    return horses.filter((horse) =>
      [
        horse.name,
        horse.name_arabic,
        horse.rfid_chip_number,
        horse.breed_english,
        horse.breed_arabic,
        horse.color_english,
        horse.color_arabic,
        horse.stable_name,
        horse.room_name,
      ].some((item) => normalize(item).includes(q))
    );
  }, [horses, query]);

  const visibleRows = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage]
  );

  const handleSearch = (event) => {
    setQuery(event.target.value);
    setPage(0);
  };

  const labelFor = (horse, enKey, arKey) =>
    (lang === 'ar' ? horse[arKey] || horse[enKey] : horse[enKey] || horse[arKey]) || '-';

  const stats = useMemo(
    () => ({
      total: horses.length,
      rfid: horses.filter((horse) => horse.rfid_chip_number).length,
      accommodated: horses.filter((horse) => horse.room_name || horse.stable_name).length,
      mares: horses.filter((horse) => horse.gender === 'M').length,
      stallions: horses.filter((horse) => horse.gender === 'S').length,
    }),
    [horses]
  );

  if (loading) {
    return (
      <Box sx={{ ...smartPageSx, display: 'grid', placeItems: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box dir={dir} sx={{ ...smartPageSx, px: { xs: 2, md: 3 }, py: 3 }}>
      <Box sx={{ maxWidth: 1440, mx: 'auto' }}>
        <Paper elevation={0} sx={{ ...smartCardSx, p: { xs: 2, md: 3 }, mb: 2.5, background: 'linear-gradient(135deg, #fffdf8 0%, #f4efe5 100%)' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2}>
            <Box sx={{ textAlign: alignStart }}>
              <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>
                {text.title}
              </Typography>
              <Typography sx={{ color: COLORS.muted, mt: 0.6 }}>{text.subtitle}</Typography>
            </Box>
            <TextField
              value={query}
              onChange={handleSearch}
              placeholder={text.search}
              size="small"
              sx={{
                minWidth: { xs: '100%', md: 360 },
                bgcolor: '#fff',
                '& input': { textAlign: alignStart },
              }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
          </Stack>
        </Paper>

        {error ? <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert> : null}

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 2, mb: 2.5 }}>
          <Metric dir={dir} icon={<Horse size={24} weight="fill" />} label={text.total} value={stats.total} />
          <Metric dir={dir} icon={<Horse size={24} weight="fill" />} label={text.withRfid} value={stats.rfid} color={COLORS.green} soft={COLORS.greenSoft} />
          <Metric dir={dir} icon={<Horse size={24} weight="fill" />} label={text.accommodated} value={stats.accommodated} color={COLORS.blue} soft={COLORS.blueSoft} />
          <Metric dir={dir} icon={<Horse size={24} weight="fill" />} label={text.mares} value={stats.mares} color={COLORS.rose} soft={COLORS.roseSoft} />
          <Metric dir={dir} icon={<Horse size={24} weight="fill" />} label={text.stallions} value={stats.stallions} color={COLORS.warning} soft={COLORS.warningSoft} />
        </Box>

        <Paper elevation={0} sx={{ ...smartCardSx, overflow: 'hidden' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={1.5} sx={{ p: 2 }}>
            <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>
              {filtered.length} {text.records}
            </Typography>
          </Stack>

          {filtered.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography sx={{ color: COLORS.muted }}>{text.noData}</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 'calc(100vh - 390px)', minHeight: 320, direction: dir }}>
              <Table stickyHeader sx={{ minWidth: 880 }}>
                <TableHead>
                  <TableRow>
                    {[text.horse, text.rfid, text.gender, text.breed, text.color, text.location, text.details].map((label) => (
                      <TableCell
                        key={label}
                        sx={{
                          bgcolor: COLORS.gold,
                          color: '#fff',
                          fontWeight: 900,
                          whiteSpace: 'nowrap',
                          textAlign: alignStart,
                        }}
                      >
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.map((horse) => (
                    <TableRow
                      key={horse.horse_id}
                      hover
                      sx={{ '& td': { borderColor: COLORS.line, color: COLORS.text } }}
                    >
                      <TableCell sx={{ minWidth: 260, textAlign: alignStart }}>
                        <Stack direction="row" alignItems="center" justifyContent="flex-start" sx={{ gap: 1.3 }}>
                          <Avatar src={getImageSrc(horse.image)} sx={{ width: 46, height: 46, bgcolor: COLORS.goldSoft, color: COLORS.gold, fontWeight: 900 }}>
                            {horseName(horse, lang).charAt(0)}
                          </Avatar>
                          <Box sx={{ minWidth: 0, textAlign: alignStart }}>
                            <Typography noWrap sx={{ color: COLORS.text, fontWeight: 900 }}>
                              {horseName(horse, lang)}
                            </Typography>
                            <Typography noWrap variant="caption" sx={{ color: COLORS.muted }}>
                              {lang === 'ar' ? horse.name || '-' : horse.name_arabic || '-'}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', textAlign: alignStart }}>
                        <Chip size="small" label={horse.rfid_chip_number || '-'} sx={{ bgcolor: COLORS.cardSoft, color: COLORS.text, fontWeight: 800 }} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', textAlign: alignStart }}>
                        <Chip
                          size="small"
                          label={horse.gender === 'M' ? text.mares : horse.gender === 'S' ? text.stallions : horse.gender || '-'}
                          sx={{ bgcolor: horse.gender === 'M' ? COLORS.roseSoft : COLORS.blueSoft, color: COLORS.text, fontWeight: 800 }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: alignStart }}>{labelFor(horse, 'breed_english', 'breed_arabic')}</TableCell>
                      <TableCell sx={{ textAlign: alignStart }}>{labelFor(horse, 'color_english', 'color_arabic')}</TableCell>
                      <TableCell sx={{ minWidth: 170, textAlign: alignStart }}>
                        {[horse.stable_name, horse.room_name].filter(Boolean).join(' - ') || '-'}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton title={text.details} onClick={() => navigate(`/my-horses/${horse.horse_id}`)} sx={{ color: COLORS.gold }}>
                          <OpenInNewIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 15, 25, 50]}
          />
        </Paper>
      </Box>
    </Box>
  );
};

export default ClientHorseList;
